import React, { useDeferredValue, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { LoadingSection } from 'components';
import { LineBarChart } from 'dashboard409a/components';
import { buildLineChartData, buildLineChartOptions } from './compute/chartData';
import { computeMultipleSeries, rateOfChangeSeries } from './compute/metrics';
import { CompRef, DEFAULT_RANGE_KEY, MULTIPLE_OPTIONS, SERIES_COLORS } from './constants';
import { useChartSeries } from './hooks';
import RangeFilter from './RangeFilter';

interface RateOfChangesChartProps {
  comps: CompRef[];
}

// Percent change of the selected multiple since the start of the window, one
// line per company. Colors follow the comp's fixed position; past the palette
// length the line dashes as the secondary distinction (legend carries
// identity either way).
const RateOfChangesChart = ({ comps }: RateOfChangesChartProps) => {
  const [rangeKey, setRangeKey] = useState<string>(DEFAULT_RANGE_KEY);
  const [multipleKey, setMultipleKey] = useState<string>(MULTIPLE_OPTIONS[1].key);
  const { byCompany, hasData, isLoading, isFetching, isError } = useChartSeries(comps, rangeKey);
  // Defer the expensive chart rebuild so range switches stay responsive.
  const deferredByCompany = useDeferredValue(byCompany);

  const multiple = MULTIPLE_OPTIONS.find(option => option.key === multipleKey) ?? MULTIPLE_OPTIONS[0];

  const chartData = useMemo(() => {
    const multiples = computeMultipleSeries(deferredByCompany, multiple.denominator);
    const specs = comps
      .map((comp, index) => ({
        label: comp.label,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        dashed: index >= SERIES_COLORS.length,
        series: rateOfChangeSeries(multiples[comp.ticker] ?? []),
      }))
      .filter(spec => spec.series.length);
    return buildLineChartData(specs);
  }, [deferredByCompany, comps, multiple]);

  // One line per company is too many for an index-mode tooltip list.
  const options = useMemo(() => buildLineChartOptions(value => `${value.toFixed(1)}%`, { nearestTooltip: true }), []);

  const handleMultipleChange = (event: SelectChangeEvent) => {
    setMultipleKey(event.target.value);
  };

  return (
    <Box>
      <Typography sx={{ color: 'text.primary', fontSize: '18px', fontWeight: 700, mb: 2 }} variant="subtitle2">
        Rate of Changes
      </Typography>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: 'space-between',
          mb: 1,
        }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
          <Typography color="text.secondary" fontWeight={600} variant="body2">
            Metric
          </Typography>
          <Select size="small" value={multipleKey} onChange={handleMultipleChange}>
            {MULTIPLE_OPTIONS.map(option => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <RangeFilter value={rangeKey} onChange={setRangeKey} />
      </Box>
      {isError && hasData && rangeKey !== DEFAULT_RANGE_KEY && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          The latest refresh failed. Showing previously loaded data.
        </Alert>
      )}
      <Box sx={{ height: 320, position: 'relative' }}>
        {isError && !hasData && (
          <Typography color="error" sx={{ my: 2 }}>
            Market data is unavailable for this range.
          </Typography>
        )}
        {(!isError || hasData) && isLoading && <LoadingSection />}
        {(!isError || hasData) && !isLoading && (
          <>
            <LineBarChart type="line" data={chartData} options={options} />
            {isFetching && (
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.6)',
                  display: 'flex',
                  inset: 0,
                  justifyContent: 'center',
                  position: 'absolute',
                }}>
                <CircularProgress size={28} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default RateOfChangesChart;
